import React, { useState } from 'react';
import { useRouter } from 'next/router';
import CbtRoundTableHeader from 'app.feature/academy/cbt.detail/component/CbtRoundTableHeader';
import CbtRoundTableFooter from 'app.feature/academy/cbt.detail/component/CbtDetailMoreButton';
import { convertExamDetailQueryString } from 'app.feature/academy/cbt.detail/module/convertExamDetailQueryString';
import { useRecoilValue } from 'recoil';
import { projectConfigState } from 'recoil/Academy/projectConfigState';
import { useCurrentUser } from 'src/app.hooks/useCurrentUser';
import CbtRoundTableMobile from 'app.feature/academy/cbt.detail/component/CbtRoundTableMobile';
import CbtRoundTablePc from 'app.feature/academy/cbt.detail/component/CbtRoundTablePc';
import { TExamsInfo } from 'app.type/examsInfo';
import { TExamsDetails } from 'app.type/examsDetails';
import { useQuery } from '@tanstack/react-query';
import ExamApi from 'lib/api/exam';
import UserApi from 'lib/api/users';
import { ParsedUrlQuery } from 'querystring';
import { TExams } from 'app.type/exams';

const CbtRoundTable = () => {
  const router = useRouter();
  const { sorts, examIdx } = parseQuery(router.query);
  
  const [showAll, setShowAll] = useState(false);
  const { handleLoginRedirect } = useRecoilValue(projectConfigState);
  const { user } = useCurrentUser();

  const { data: examInfo = null } = useQuery<TExamsInfo>({
    queryKey: [`/apiV2/exams/${examIdx}`],
    queryFn: () => ExamApi.getExamInfo(examIdx),
  });

  const examTitle = examInfo?.examName;
  const examDetailQuery = convertExamDetailQueryString({ sorts });
  
  const { data: examDetail = [] } = useQuery<TExamsDetails[]>({
    queryKey: [`/apiV2/exams/${examIdx}/exam-details`, examDetailQuery],
    queryFn: () => ExamApi.getExamDetails(examIdx, examDetailQuery),
  });

  const { data: userPurchasedExams = [] } = useQuery<TExams[]>({
    queryKey: ['/apiV2/user/me/exams'],
    queryFn: UserApi.getUserPurchases,
    enabled: !!user,
  });

  const displayedExams = showAll ? examDetail : examDetail?.slice(0, 11);
  const handleShowMore = () => {
    setShowAll(true);
  };

  const handleSolveButtonClick = (examDetailIdx: number) => {
    if (!user) {
      return router.push(handleLoginRedirect(window.location.href));
    }

    if (!hasExamAccess(examDetailIdx)) {
      return alert("해당 시험은 구매 후 이용할 수 있습니다.");
    }
    
    router.push(`/cbt/select/${examDetailIdx}`);
  };
  
  const hasExamAccess = (examDetailIdx: number): boolean => {
    const examDetailInfo = examDetail.find(
      (detail) => detail.examDetailIdx === examDetailIdx,
    );

    if (!examDetailInfo) {
      return false; // 시험 없을 때 우선 접근 불가 처리
    }

    const isPaidExam = examDetailInfo.publicLevel > 0;
    if (!isPaidExam) {
      return true;
    }

    const hasPurchased = userPurchasedExams.some((exam) => exam.examIdx === examIdx);
    return hasPurchased;
  }


  return (
    <>
      <CbtRoundTableHeader examDetail={examDetail} />
      <CbtRoundTablePc
        examInfo={examInfo}
        examDetail={examDetail}
        displayedExams={displayedExams}
        handleSolveButtonClick={handleSolveButtonClick}
      />
      <CbtRoundTableMobile
        examTitle={examTitle}
        examDetail={examDetail}
        handleSolveButtonClick={handleSolveButtonClick}
      />
      {!showAll && examDetail.length > 11 && (
        <CbtRoundTableFooter handleShowMore={handleShowMore} arrow={'down'} />
      )}
    </>
  );
};

export default CbtRoundTable;

const parseQuery = (query: ParsedUrlQuery) => {
  return {
    sorts: query.sorts || 'examDate,desc',
    examIdx: Number(query.examIdx),
  };
};
